// src/components/dashboard/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Stack,
    Text,
    useToast,
    VStack,
    HStack,
    Divider,
    Flex,
    Spinner,
    Badge,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    useBreakpointValue,
    InputGroup,
    InputRightElement,
} from '@chakra-ui/react';
import { supabase } from '../../config/supabaseClient';
import { FiUser, FiMail, FiPhone, FiFileText, FiCreditCard, FiCheck, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import InputMask from 'react-input-mask';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { formatPhoneNumber, normalizePhoneNumber } from '../../utils/formatters'; // Importar as funções utilitárias

// Modified to accept props from Dashboard
const ProfilePage = ({ userId, onProfileUpdate, onBack }) => {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        cpf_cnpj: '',
        creditos: 0,
        codigo_cupom: '',
        comissao_total: 0,
        comissao_disponivel: 0,
        postal_code: '',
        address: '',
        address_number: '',
        city: '',
        state: '',
        neighborhood: '',
        complement: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isCpf, setIsCpf] = useState(true);
    const toast = useToast();
    const [loadingCep, setLoadingCep] = useState(false);

    // Busca CEP
    const handleSearchCep = async () => {
        const cep = profile.postal_code.replace(/\D/g, '');
        if (cep.length !== 8) {
            toast({
                title: 'CEP inválido',
                description: 'Por favor, digite um CEP válido com 8 dígitos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoadingCep(true);

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast({
                    title: 'CEP não encontrado',
                    description: 'O CEP informado não foi encontrado.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            setProfile(prev => ({
                ...prev,
                address: data.logradouro,
                neighborhood: data.bairro,
                city: data.localidade,
                state: data.uf
            }));

            toast({
                title: 'Endereço encontrado',
                description: 'Os dados de endereço foram preenchidos automaticamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast({
                title: 'Erro na busca',
                description: 'Ocorreu um erro ao buscar o CEP.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingCep(false);
        }
    };

    // Responsividade
    const isMobile = useBreakpointValue({ base: true, md: false });

    useEffect(() => {
        const getUser = async () => {
            // If userId is provided as a prop, use it directly
            if (userId) {
                const { data: { user } } = await supabase.auth.getUser();
                setUser(user);
                await fetchProfile(userId);
                setLoading(false);
                return;
            }

            // Otherwise fallback to original logic
            const { data: { user } } = await supabase.auth.getUser();

            if (user) {
                setUser(user);
                await fetchProfile(user.id);
            }

            setLoading(false);
        };

        getUser();
    }, [userId]);

    useEffect(() => {
        // Determinar se é CPF ou CNPJ com base no comprimento
        if (profile.cpf_cnpj) {
            const cleanCpfCnpj = profile.cpf_cnpj.replace(/\D/g, '');
            setIsCpf(cleanCpfCnpj.length <= 11);
        }
    }, [profile.cpf_cnpj]);

    const fetchProfile = async (userId) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();

            if (error) {
                console.error('Erro ao carregar perfil:', error);
                return;
            }

            setProfile({
                name: data.name || '',
                email: data.email || user?.email || '',
                phone: formatPhoneNumber(data.phone) || '',
                cpf_cnpj: data.cpf_cnpj || '',
                creditos: data.creditos || 0,
                codigo_cupom: data.codigo_cupom || '',
                comissao_total: data.comissao_total || 0,
                comissao_disponivel: data.comissao_disponivel || 0,
                postal_code: data.postal_code || '',
                address: data.address || '',
                address_number: data.address_number || '',
                city: data.city || '',
                state: data.state || '',
                neighborhood: data.neighborhood || '',
                complement: data.complement || ''
            });
        } catch (err) {
            console.error('Erro inesperado ao buscar perfil:', err);
        }
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProfile({
            ...profile,
            [name]: value
        });
    };

    const toggleDocumentType = () => {
        setIsCpf(!isCpf);
        setProfile({
            ...profile,
            cpf_cnpj: '' // Limpa o campo quando troca o tipo
        });
    };

    const validateForm = () => {
        if (!profile.name || !profile.phone) {
            toast({
                title: 'Campos obrigatórios',
                description: 'Por favor, preencha todos os campos obrigatórios.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Validação de CPF/CNPJ
        if (profile.cpf_cnpj) {
            const cpfCnpjClean = profile.cpf_cnpj.replace(/\D/g, '');
            const isCpfByLength = cpfCnpjClean.length <= 11;

            const isValid = isCpfByLength
                ? cpf.isValid(profile.cpf_cnpj)
                : cnpj.isValid(profile.cpf_cnpj);

            if (!isValid) {
                toast({
                    title: isCpfByLength ? 'CPF inválido' : 'CNPJ inválido',
                    description: `Por favor, digite um ${isCpfByLength ? 'CPF' : 'CNPJ'} válido.`,
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return false;
            }
        }

        // Resto da validação
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setSaving(true);

        try {           
            const { error } = await supabase
                .from('profiles')
                .update({
                    name: profile.name,
                    phone: normalizePhoneNumber(profile.phone),
                    cpf_cnpj: profile.cpf_cnpj,
                    postal_code: profile.postal_code,
                    address: profile.address,
                    address_number: profile.address_number,
                    city: profile.city,
                    state: profile.state,
                    neighborhood: profile.neighborhood,
                    complement: profile.complement
                })
                .eq('id', user.id);

            if (error) throw error;

            toast({
                title: 'Perfil atualizado',
                description: 'Suas informações foram atualizadas com sucesso.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Call the onProfileUpdate prop if it exists
            if (typeof onProfileUpdate === 'function') {
                onProfileUpdate();
            }
        } catch (error) {
            console.error('Erro ao atualizar perfil:', error);
            toast({
                title: 'Erro ao atualizar',
                description: error.message || 'Ocorreu um erro ao atualizar seu perfil.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container maxW="container.md" centerContent py={10}>
                <Spinner size="xl" />
            </Container>
        );
    }

    return (
        <Container maxW="container.md" py={8} px={{ base: 4, md: 8 }}>
            <Box bg="white" p={{ base: 4, md: 8 }} borderRadius="lg" boxShadow="md">
                <Heading as="h1" size="xl" mb={6} color="brand.primary">
                    <Flex align="center" justify="space-between">
                        <Flex align="center">
                            <FiUser style={{ marginRight: '10px' }} />
                            Meu Perfil
                        </Flex>
                        {onBack && (
                            <Button
                                leftIcon={<FiArrowLeft />}
                                variant="ghost"
                                onClick={onBack}
                                size="sm"
                            >
                                Voltar
                            </Button>
                        )}
                    </Flex>
                </Heading>

                {!profile.cpf_cnpj && (
                    <Alert status="warning" mb={6}>
                        <AlertIcon />
                        <AlertTitle mr={2}>Atenção!</AlertTitle>
                        <AlertDescription>
                            Para realizar pagamentos, é necessário preencher seu CPF ou CNPJ.
                        </AlertDescription>
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <VStack spacing={6} align="stretch">
                        {/* Layout responsivo - muda para coluna em dispositivos móveis */}
                        <Stack
                            direction={{ base: 'column', md: 'row' }}
                            spacing={{ base: 6, md: 6 }}
                            align={{ base: 'stretch', md: 'flex-start' }}
                        >
                            <Box flex="1">
                                <Heading size="md" mb={4}>Informações Pessoais</Heading>
                                <Stack spacing={4}>
                                    <FormControl id="name" isRequired>
                                        <FormLabel>Nome completo</FormLabel>
                                        <Input
                                            name="name"
                                            value={profile.name}
                                            onChange={handleInputChange}
                                        />
                                    </FormControl>

                                    <FormControl id="email" isDisabled>
                                        <FormLabel>Email</FormLabel>
                                        <Input
                                            name="email"
                                            value={profile.email}
                                            readOnly
                                        />
                                        <Text fontSize="sm" color="gray.500" mt={1}>
                                            O email não pode ser alterado.
                                        </Text>
                                    </FormControl>

                                    <FormControl id="phone" isRequired>
                                        <FormLabel>Telefone</FormLabel>
                                        <Input
                                            as={InputMask}
                                            mask="(99) 99999-9999"
                                            name="phone"
                                            value={profile.phone}
                                            onChange={handleInputChange}
                                            placeholder="(00) 00000-0000"
                                        />
                                    </FormControl>

                                    <FormControl id="cpf_cnpj" isRequired>
                                        <Flex justify="space-between" align="center">
                                            <FormLabel marginBottom="0">{isCpf ? 'CPF' : 'CNPJ'}</FormLabel>
                                            <Button size="xs" onClick={toggleDocumentType} variant="outline">
                                                Mudar para {isCpf ? 'CNPJ' : 'CPF'}
                                            </Button>
                                        </Flex>
                                        <Input
                                            as={InputMask}
                                            mask={isCpf ? "999.999.999-99" : "99.999.999/9999-99"}
                                            name="cpf_cnpj"
                                            value={profile.cpf_cnpj}
                                            onChange={handleInputChange}
                                            placeholder={isCpf ? "000.000.000-00" : "00.000.000/0000-00"}
                                            mt={2}
                                        />
                                    </FormControl>
                                </Stack>
                            </Box>

                            {/* Dados de Endereço no ProfilePage.jsx */}
                            <Box flex="1">
                                <Heading size="md" mb={4}>Endereço</Heading>
                                <Stack spacing={4}>
                                    <FormControl id="postal_code">
                                        <FormLabel>CEP</FormLabel>
                                        <InputGroup>
                                            <Input
                                                as={InputMask}
                                                mask="99999-999"
                                                name="postal_code"
                                                value={profile.postal_code}
                                                onChange={handleInputChange}
                                                placeholder="00000-000"
                                            />
                                            <InputRightElement width="4.5rem">
                                                <Button
                                                    h="1.75rem"
                                                    size="sm"
                                                    onClick={handleSearchCep}
                                                    isLoading={loadingCep}
                                                >
                                                    Buscar
                                                </Button>
                                            </InputRightElement>
                                        </InputGroup>
                                    </FormControl>

                                    <FormControl id="address">
                                        <FormLabel>Logradouro</FormLabel>
                                        <Input
                                            name="address"
                                            value={profile.address}
                                            onChange={handleInputChange}
                                            placeholder="Rua, Avenida, etc."
                                        />
                                    </FormControl>

                                    <HStack spacing={4}>
                                        <FormControl id="address_number">
                                            <FormLabel>Número</FormLabel>
                                            <Input
                                                name="address_number"
                                                value={profile.address_number}
                                                onChange={handleInputChange}
                                                placeholder="123"
                                            />
                                        </FormControl>

                                        <FormControl id="complement">
                                            <FormLabel>Complemento</FormLabel>
                                            <Input
                                                name="complement"
                                                value={profile.complement}
                                                onChange={handleInputChange}
                                                placeholder="Apto, Bloco, etc."
                                            />
                                        </FormControl>
                                    </HStack>

                                    <FormControl id="neighborhood">
                                        <FormLabel>Bairro</FormLabel>
                                        <Input
                                            name="neighborhood"
                                            value={profile.neighborhood}
                                            onChange={handleInputChange}
                                            placeholder="Bairro"
                                        />
                                    </FormControl>

                                    <HStack spacing={4}>
                                        <FormControl id="city">
                                            <FormLabel>Cidade</FormLabel>
                                            <Input
                                                name="city"
                                                value={profile.city}
                                                onChange={handleInputChange}
                                                placeholder="Cidade"
                                            />
                                        </FormControl>

                                        <FormControl id="state">
                                            <FormLabel>Estado</FormLabel>
                                            <Input
                                                name="state"
                                                value={profile.state}
                                                onChange={handleInputChange}
                                                placeholder="UF"
                                                maxLength={2}
                                            />
                                        </FormControl>
                                    </HStack>
                                </Stack>
                            </Box>

                            <Box flex="1">
                                <Heading size="md" mb={4}>Informações da Conta</Heading>
                                <VStack align="stretch" spacing={4}>
                                    <Box p={4} borderWidth="1px" borderRadius="md">
                                        <Text fontWeight="bold" mb={1}>Créditos disponíveis</Text>
                                        <Flex align="center">
                                            <FiCreditCard style={{ marginRight: '8px' }} />
                                            <Text fontSize="2xl" fontWeight="bold" color="green.500">
                                                {profile.creditos || 0}
                                            </Text>
                                        </Flex>
                                    </Box>

                                    <Box p={4} borderWidth="1px" borderRadius="md">
                                        <Text fontWeight="bold" mb={1}>Seu código de cupom</Text>
                                        <Text color="blue.500" fontWeight="bold" fontSize="xl">
                                            {profile.codigo_cupom || 'Não disponível'}
                                        </Text>
                                        <Text fontSize="sm" mt={1} color="gray.600">
                                            Compartilhe este código para ganhar comissões.
                                        </Text>
                                    </Box>

                                    {(profile.comissao_total > 0 || profile.comissao_disponivel > 0) && (
                                        <Box p={4} borderWidth="1px" borderRadius="md">
                                            <Text fontWeight="bold" mb={1}>Comissões</Text>
                                            <HStack justify="space-between" mb={1}>
                                                <Text>Total acumulado:</Text>
                                                <Text fontWeight="medium">R$ {profile.comissao_total.toFixed(2)}</Text>
                                            </HStack>
                                            <HStack justify="space-between">
                                                <Text>Disponível para saque:</Text>
                                                <Text fontWeight="medium" color="green.500">
                                                    R$ {profile.comissao_disponivel.toFixed(2)}
                                                </Text>
                                            </HStack>
                                        </Box>
                                    )}
                                </VStack>
                            </Box>
                        </Stack>

                        <Divider my={4} />

                        <Flex justify="flex-end">
                            <Button
                                type="submit"
                                colorScheme="blue"
                                isLoading={saving}
                                leftIcon={<FiCheck />}
                            >
                                Salvar alterações
                            </Button>
                        </Flex>
                    </VStack>
                </form>
            </Box>
        </Container>
    );
};

export default ProfilePage;